import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug) : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-foreground-50 text-2xl font-bold mb-4">文章不存在</h1>
          <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        url: `http://gjxfl.cn/article/${article.slug}`,
        image: article.imageUrl,
        publisher: {
          '@type': 'Organization',
          name: '黑料718',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://gjxfl.cn/' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `http://gjxfl.cn/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `http://gjxfl.cn/article/${article.slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <title>{article.title} | 黑料718</title>
      <meta name="description" content={article.summary} />
      <meta name="keywords" content={article.tags.join(', ')} />
      <meta property="og:title" content={`${article.title} | 黑料718`} />
      <meta property="og:description" content={article.summary} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`http://gjxfl.cn/article/${article.slug}`} />
      <meta property="og:image" content={article.imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <link rel="canonical" href={`http://gjxfl.cn/article/${article.slug}`} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article>
            <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
            </section>

            <section className="w-full max-w-[900px] mx-auto px-6 md:px-10 -mt-32 relative z-10">
              <nav className="flex items-center space-x-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-400 transition-colors whitespace-nowrap">{article.category}</Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <span className="text-foreground-300 truncate">{article.title}</span>
              </nav>

              <div className="mb-8">
                <span className="inline-block px-3 py-1 bg-accent-500/10 border border-accent-500/20 rounded-full text-accent-400 text-xs font-medium mb-4">
                  独家爆料
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-50 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-foreground-400 text-base leading-relaxed mb-6">{article.summary}</p>

                <div className="flex flex-wrap items-center gap-4 text-foreground-500 text-sm border-b border-background-200/60 pb-6">
                  <span className="flex items-center space-x-1">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{article.author}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{article.date}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{article.views >= 1000 ? `${(article.views / 1000).toFixed(0)}k 浏览` : `${article.views} 浏览`}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <i className="ri-chat-1-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{article.comments >= 1000 ? `${(article.comments / 1000).toFixed(0)}k 评论` : `${article.comments} 评论`}</span>
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 border border-background-200/60 rounded-full text-foreground-500 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="prose-custom text-foreground-300 text-base leading-relaxed space-y-4">
                {article.content.split('\n').filter(Boolean).map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </section>

            {related.length > 0 && (
              <section className="w-full max-w-[1400px] mx-auto px-6 md:px-10 py-16">
                <div className="border-t border-background-200/60 pt-12">
                  <h2 className="font-heading text-2xl font-black text-foreground-50 mb-2">
                    相关<strong className="text-primary-400">黑料推荐</strong>
                  </h2>
                  <p className="text-foreground-500 text-sm mb-8">你可能对这些爆料也感兴趣</p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/article/${item.slug}`}
                        className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                      >
                        <div className="relative overflow-hidden">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            title={item.title}
                            className="w-full h-44 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent"></div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-foreground-100 text-sm font-semibold leading-snug mb-2 group-hover:text-primary-400 transition-colors line-clamp-2">
                            {item.title}
                          </h3>
                          <div className="flex items-center justify-between text-foreground-600 text-xs">
                            <span>{item.date}</span>
                            <span>{item.views >= 1000 ? `${(item.views / 1000).toFixed(0)}k 浏览` : `${item.views} 浏览`}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>

      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </>
  );
}